import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
    ChartAccount,
    LocaleCode,
    OperationHistoryVersion,
    OperationTemplateHistory,
} from '@/api/models';
import { useChartOfAccounts, useOperationHistoryQuery } from '@/api/queries';
import { resolveLocale } from '@/pages/accounting/statements/statementMeta';
import { PATH } from '@/router/PATH';
import { getAuthMethod, getUser } from '@/utils/authSession';
import {
    canEditOperations,
    canReadOperations,
    deriveOperationsAccess,
} from '../operationsAccess';
import { draftVersionOf, toTimeline, type TimelineEntry } from './historyMeta';

type UseHistoryDrawerOptions = {
    /** The template to show; `null` closes the drawer and fetches nothing. */
    operationKey: string | null;
};

export type UseHistoryDrawerResult = {
    locale: LocaleCode;
    isLoading: boolean;
    /** A failed read. Rendered inside the drawer, never thrown. */
    hasError: boolean;
    history: OperationTemplateHistory | null;
    timeline: TimelineEntry[];
    /** The chart, so a line's account id renders as a code and a name. */
    accounts: ChartAccount[];
    /** Which version cards are expanded, by version number. */
    expanded: number[];
    onToggle: (version: number) => void;
    /** The open draft, if this template has one — the footer's action. */
    draftVersion: OperationHistoryVersion | null;
    /** Whether this session could open that draft in the editor. */
    canEdit: boolean;
    /** Open the Activity tab filtered to this operation. */
    onOpenActivity: () => void;
};

/**
 * Drives the History drawer.
 *
 * **Self-served**, like the recorded-operation drawer beside it: it owns the
 * read for the key it was handed rather than being passed a row its host
 * already had. That is what lets both hosts — the catalog and the editor — each
 * mount their own instance without either one owning the other's copy of the
 * data, which is what kept this a sub-page until now.
 *
 * Two reads: the history itself, and the chart — a version line names its
 * account by **id**, and without the chart the timeline would render uuids. The
 * rule every subsidiary register in this folder follows since BOS-105: a stored
 * code stops meaning what it said the moment somebody renumbers.
 *
 * **The diff is computed here, not fetched.** `toTimeline` pairs each version
 * with its predecessor and `operationDiff` does the comparing — both pure, both
 * tested without rendering anything.
 *
 * **A failed read is reported, not thrown.** The pages in this folder throw to
 * their boundary; a drawer is the one place that is wrong, because throwing
 * would replace a working catalog with a full-page error over one template that
 * would not open. The read still has to be *believed* — an unreachable history
 * and a template with one version look alike on screen — so the failure is
 * stated in the body rather than left as an empty timeline.
 */
export const useHistoryDrawer = ({
    operationKey,
}: UseHistoryDrawerOptions): UseHistoryDrawerResult => {
    const navigate = useNavigate();
    const { i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);

    // A PIN or kiosk session is refused by every accounting endpoint whatever
    // role it carries. Its host renders an access notice instead of the catalog,
    // so this drawer cannot be opened there — the gate is belt and braces, and
    // it keeps the drawer from firing a request it knows will be refused.
    const access = React.useMemo(
        () => deriveOperationsAccess(getUser(), getAuthMethod()),
        [],
    );
    const isOpen = operationKey !== null && canReadOperations(access);

    const query = useOperationHistoryQuery(operationKey ?? '', isOpen);
    const history = query.data?.success ? query.data.response : null;
    const hasError = query.data ? !query.data.success : Boolean(query.error);

    const chart = useChartOfAccounts({ locale, enabled: isOpen });

    const timeline = React.useMemo(
        () => (history ? toTimeline(history.versions) : []),
        [history],
    );

    /**
     * The newest version opens expanded; the rest start collapsed.
     *
     * A timeline of eight versions all expanded is a wall, and the change
     * somebody came to look at is almost always the last one. Keyed on the
     * template and its newest version, so reopening the drawer over a different
     * operation does not inherit the previous one's expansion.
     */
    const [expanded, setExpanded] = React.useState<number[]>([]);
    const seededFor = React.useRef<string | null>(null);
    const newest = timeline[0]?.version.version ?? null;

    React.useEffect(() => {
        const seedKey = `${operationKey ?? '-'}:${newest ?? '-'}`;
        if (newest === null || seededFor.current === seedKey) {
            return;
        }
        seededFor.current = seedKey;
        setExpanded([newest]);
    }, [operationKey, newest]);

    const onToggle = React.useCallback((version: number) => {
        setExpanded((current) =>
            current.includes(version)
                ? current.filter((v) => v !== version)
                : [...current, version],
        );
    }, []);

    const onOpenActivity = React.useCallback(() => {
        if (operationKey === null) {
            return;
        }
        navigate(
            PATH.ACCOUNTING.OPERATIONS.BIND_ACTIVITY_FOR_KEY(operationKey),
        );
    }, [navigate, operationKey]);

    return {
        locale,
        // The chart is part of the screen, not a decoration: without it every
        // fixed line renders as a bare uuid. So the drawer loads until both land.
        isLoading: isOpen && !hasError && (history === null || chart.isLoading),
        hasError,
        history,
        timeline,
        accounts: chart.data ?? [],
        expanded,
        onToggle,
        draftVersion: history ? draftVersionOf(history.versions) : null,
        canEdit: canEditOperations(access),
        onOpenActivity,
    };
};
